type AnalyticsEventPayload = {
  eventType: string;
  page?: string;
  label?: string;
  metadata?: Record<string, unknown>;
  // Top-level geo fields — written as dedicated columns, not just inside metadata
  city?: string;
  region?: string;
  country?: string;
  country_code?: string;
  lat?: number | null;
  lng?: number | null;
};

export type EventRecord = {
  id?: string | number;
  event_type?: string;
  page?: string;
  label?: string;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
  // Top-level geo columns (present on new rows; undefined on old rows)
  city?: string | null;
  region?: string | null;
  country?: string | null;
  country_code?: string | null;
  lat?: number | null;
  lng?: number | null;
};

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const EVENTS_TABLE = process.env.REACT_APP_SUPABASE_ANALYTICS_TABLE || 'admin_analytics_events';

function getWriteHeaders() {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return null;
  return {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
    Prefer: 'return=minimal'
  };
}

function getReadHeaders() {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return null;
  return {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };
}

export function isSupabaseAnalyticsEnabled() {
  return Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
}

export async function recordAdminAnalyticsEvent(payload: AnalyticsEventPayload) {
  const headers = getWriteHeaders();
  if (!headers || !SUPABASE_URL) return;

  const body = {
    event_type: payload.eventType,
    page:        payload.page  || 'admin-analytics',
    label:       payload.label || null,
    metadata:    payload.metadata || {},
    // Write geo as dedicated top-level columns so Supabase can query/index them
    city:         payload.city         ?? null,
    region:       payload.region       ?? null,
    country:      payload.country      ?? null,
    country_code: payload.country_code ?? null,
    lat:          payload.lat          ?? null,
    lng:          payload.lng          ?? null,
  };

  try {
    await fetch(`${SUPABASE_URL}/rest/v1/${EVENTS_TABLE}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });
  } catch (error) {
    console.warn('Supabase analytics event could not be recorded.', error);
  }
}

export async function fetchAdminAnalyticsEvents(limit = 30): Promise<EventRecord[]> {
  const headers = getReadHeaders();
  if (!headers || !SUPABASE_URL) {
    console.warn('[Supabase] Missing URL or key — skipping fetch');
    return [];
  }

  // Select both the new top-level geo columns AND metadata (for backwards compat with old rows)
  const url = `${SUPABASE_URL}/rest/v1/${EVENTS_TABLE}?select=id,event_type,page,label,metadata,created_at,city,region,country,country_code,lat,lng&order=created_at.desc&limit=${limit}`;
  console.log('[Supabase] Fetching:', url);

  try {
    const response = await fetch(url, { method: 'GET', headers });
    console.log('[Supabase] Response status:', response.status);

    if (!response.ok) {
      const text = await response.text();
      console.warn('[Supabase] Error body:', text);
      throw new Error(`Supabase request failed with ${response.status}`);
    }

    const data = await response.json();
    console.log('[Supabase] Events received:', data?.length);
    return data as EventRecord[];
  } catch (error) {
    console.warn('Supabase analytics events could not be loaded.', error);
    return [];
  }
}
